// ============================================================================
// roundState.js — the SINGLE source of truth for susu round/turn/funding state.
//
// PURE + SIDE-EFFECT-FREE. No Supabase, no I/O, no dates. It takes already-fetched
// rows (rounds / payments / payouts) and a hand, and returns derived state. This
// collapses the three previously-duplicated implementations into one:
//   - canSendPayout        (was inline in GroupPaymentDashboard)
//   - isTurnToCollect      (was inline in MemberDashboard, date-based)
//   - getCollectionStatus  (was inline in RotationView, payout+position based)
//
// It reads the NEW model (rounds.collector_hand_id / status, round-scoped
// payments, per-cycle confirmed payouts) and NEVER the deprecated
// hands.collected_amount / collected_at / contribution_status.
//
// Turn order is authoritative from the `rounds` table (there is exactly one live
// round per group), so "who is up now" is positional — not recomputed from dates.
// That is what guarantees exactly one `up_now` even when several positions look
// "past due" by the calendar.
//
// Data shapes (relevant fields):
//   hand    : { id, group_id, position_number, member_db_id }
//   round   : { id, group_id, cycle_number, round_number, collector_hand_id,
//               status: 'scheduled'|'open'|'funded'|'paid_out'|'confirmed',
//               contributor_count }
//   payment : { hand_id, round_id, cycle_number, round_number, status }
//   payout  : { hand_id, round_id, cycle_number, status:'pending_confirmation'|
//               'confirmed'|'disputed' }
// ============================================================================

import { PAYMENT_STATUSES } from '../constants/paymentStatuses';

/** The six mutually-exclusive member states (exactly one per member at a time). */
export const MEMBER_STATE = Object.freeze({
  CONTRIBUTE_NOW: 'contribute_now', // round open, this member still owes a contribution
  WAITING:        'waiting',        // contributed; not their turn, not up next
  UP_NEXT:        'up_next',        // collects in the round immediately after the live one
  UP_NOW:         'up_now',         // is the live round's collector (their turn)
  COLLECTED:      'collected',      // already collected this cycle AND square this round
  CYCLE_COMPLETE: 'cycle_complete', // the cycle is finished (all rounds confirmed)
});

/** Round lifecycle statuses (mirror the DB check constraint). */
export const ROUND_STATUS = Object.freeze({
  SCHEDULED: 'scheduled',
  OPEN:      'open',
  FUNDED:    'funded',
  PAID_OUT:  'paid_out',
  CONFIRMED: 'confirmed',
});

// A round is "live" (the in-progress collection) in any of these statuses.
const LIVE_STATUSES = [ROUND_STATUS.OPEN, ROUND_STATUS.FUNDED, ROUND_STATUS.PAID_OUT];
// A payout can still be SENT while the round is open/funded (paid_out => already sent).
const SENDABLE_STATUSES = [ROUND_STATUS.OPEN, ROUND_STATUS.FUNDED];

// Payments that count toward FUNDING (real money confirmed in).
const FUNDED_PAYMENT_STATUSES = [PAYMENT_STATUSES.PAID, PAYMENT_STATUSES.COVERED];
// Payments meaning the member has DONE THEIR PART this round (incl. awaiting review).
const ACTED_PAYMENT_STATUSES = [
  PAYMENT_STATUSES.PAID,
  PAYMENT_STATUSES.COVERED,
  PAYMENT_STATUSES.MEMBER_SUBMITTED,
];

// Payout statuses that mean this hand has HAD ITS TURN this cycle — a genuine
// collector confirmation OR an admin override (the money went out either way).
// 'advanced_unconfirmed' is set by admin_advance_round; it counts as collected so
// the collector isn't left mis-stated, but it never claims the member confirmed.
const COLLECTED_PAYOUT_STATUSES = ['confirmed', 'advanced_unconfirmed'];

// ---- id helpers -----------------------------------------------------------
// int8 columns can surface as number OR string across the stack; compare loosely
// (mirrors the existing String()-based comparisons in the components).
const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const num = (v) => (v == null ? null : Number(v));

// Scope a row set to the hand's group (defensive; callers should pass group data).
const scopeToGroup = (rows, groupId) =>
  groupId == null ? rows : rows.filter((r) => sameId(r.group_id, groupId));

// Pick the single live round (prefer the highest cycle, then lowest round_number).
const pickLive = (rounds) =>
  [...rounds]
    .filter((r) => LIVE_STATUSES.includes(r.status))
    .sort((a, b) => num(b.cycle_number) - num(a.cycle_number) || num(a.round_number) - num(b.round_number))[0]
    || null;

/**
 * The live round for a group, or null if none is in progress.
 * @param {Array} rounds
 * @param {string|number} [groupId]
 */
export function getLiveRound(rounds = [], groupId) {
  return pickLive(scopeToGroup(rounds, groupId));
}

/**
 * Is the group's current cycle finished? True when there is NO live round AND the
 * latest cycle's rounds are all confirmed — the terminal state the DB reaches when
 * groups.current_round_id is null and every position has collected. This is the
 * SAME condition resolveMemberRoundState uses for CYCLE_COMPLETE, exported so the
 * admin/rotation views can render the terminal state too.
 */
export function isCycleComplete(rounds = [], groupId) {
  const scoped = scopeToGroup(rounds, groupId);
  if (!scoped.length) return false;      // no rounds = not started
  if (pickLive(scoped)) return false;    // a live round = in progress
  const latestCycle = Math.max(...scoped.map((r) => num(r.cycle_number)));
  const latest = scoped.filter((r) => num(r.cycle_number) === latestCycle);
  return latest.length > 0 && latest.every((r) => r.status === ROUND_STATUS.CONFIRMED);
}

// ---------------------------------------------------------------------------
// Stored per-round schedule (susu_rounds Stage 9). The date SOURCE for display —
// replaces the old start_date+frequency+position recompute. Dates are stamped
// on the rounds at cycle creation and are stable/immutable across group edits.
// ---------------------------------------------------------------------------

/**
 * The stored scheduled_collection_date of the round where `hand` is the collector,
 * within the given cycle. Returns the raw stored date value (string/Date) or null.
 */
export function getScheduledCollectionDate(hand, rounds = [], cycleNumber) {
  if (!hand) return null;
  const r = rounds.find((x) =>
    sameId(x.collector_hand_id, hand.id) &&
    (cycleNumber == null || num(x.cycle_number) === num(cycleNumber)));
  return r?.scheduled_collection_date ?? null;
}

/** The stored payment_due_date for a given round (or null). */
export function getRoundPaymentDueDate(round) {
  return round?.payment_due_date ?? null;
}

/**
 * The 1-based COLLECTION order of a hand within a cycle = the round_number of the
 * round where that hand is the collector (Stage 9). Position 1 = round-1 collector
 * = earliest collection date — NOT the original hands.position_number assignment.
 * Returns null if the hand collects in no round of that cycle (e.g. before open).
 */
export function getCollectionPosition(hand, rounds = [], cycleNumber) {
  if (!hand) return null;
  const r = rounds.find((x) =>
    sameId(x.collector_hand_id, hand.id) &&
    (cycleNumber == null || num(x.cycle_number) === num(cycleNumber)));
  return r ? num(r.round_number) : null;
}

/**
 * Format a date-ONLY value ('YYYY-MM-DD' string or a Date) as a locale date
 * string, parsed at LOCAL midnight so there is no timezone off-by-one (a plain
 * `date` column parsed via new Date('YYYY-MM-DD') is UTC midnight and can render
 * as the previous day in negative-UTC zones). Returns null for a falsy input.
 */
export function formatScheduleDate(d) {
  if (!d) return null;
  const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
  const [y, m, day] = s.split('-').map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day).toLocaleDateString();
}

/**
 * The round-scoped payment for a hand (replaces the hand_id-only getHandPayment).
 * Matches BOTH hand_id and round_id so a prior round's row is never mistaken for
 * the current one.
 */
export function getRoundPayment(hand, round, payments = []) {
  if (!hand || !round) return null;
  return payments.find((p) => sameId(p.hand_id, hand.id) && sameId(p.round_id, round.id)) || null;
}

/**
 * Is the (live) round fully funded — every contributor's payment paid/covered?
 * Uses the round's contributor_count; returns false if that is unknown.
 */
export function isCurrentRoundFunded(round, payments = []) {
  if (!round || round.contributor_count == null) return false;
  // Model B: the round's collector does NOT contribute in the round they collect,
  // so the round funds on the OTHERS (N-1). Exclude the collector's hand from the
  // paid/covered count and compare against contributor_count - 1 — mirrors the
  // engine's send_payout funding gate (Stage 12). Without this the payout can
  // never be sent (max N-1 paid can never reach N) and the money flow deadlocks.
  const funded = payments.filter(
    (p) => sameId(p.round_id, round.id)
      && !sameId(p.hand_id, round.collector_hand_id)
      && FUNDED_PAYMENT_STATUSES.includes(p.status)
  ).length;
  return funded >= num(round.contributor_count) - 1;
}

/**
 * Has this hand already collected in the given cycle? True for a collector
 * confirmation OR an admin override (both mean the payout went out).
 */
export function hasCollectedThisCycle(hand, cycleNumber, payouts = [], rounds = []) {
  if (!hand) return false;
  const byPayout = payouts.some(
    (p) => sameId(p.hand_id, hand.id) && num(p.cycle_number) === num(cycleNumber)
      && COLLECTED_PAYOUT_STATUSES.includes(p.status)
  );
  // Direct-to-collector groups have NO payouts; the round being 'confirmed' (set by
  // _advance_round) is the collected signal. In a through_admin group a confirmed
  // round ALWAYS has a confirmed/advanced payout, so byRound ⟹ byPayout there and
  // the OR is inert — Model B is unchanged. `rounds` defaults [] so callers that
  // don't pass it behave exactly as before (payout-only).
  const byRound = rounds.some(
    (r) => sameId(r.collector_hand_id, hand.id) && num(r.cycle_number) === num(cycleNumber)
      && r.status === ROUND_STATUS.CONFIRMED
  );
  return byPayout || byRound;
}

/**
 * The actual collection timestamp for a hand this cycle: the payout's confirmed /
 * advanced / sent time, or — in a direct group with no payout — the collector
 * round's confirmed_at (set by _advance_round). Returns a raw timestamp or null.
 * Model B is unchanged: the payout date short-circuits before the round fallback.
 */
export function collectedTimestamp(hand, cycleNumber, payouts = [], rounds = []) {
  if (!hand) return null;
  const p = payouts.find(
    (x) => sameId(x.hand_id, hand.id) && COLLECTED_PAYOUT_STATUSES.includes(x.status)
  );
  const fromPayout = p && (p.confirmed_at || p.advanced_at || p.date_sent);
  if (fromPayout) return fromPayout;
  const r = rounds.find(
    (x) => sameId(x.collector_hand_id, hand.id) && num(x.cycle_number) === num(cycleNumber)
      && x.status === ROUND_STATUS.CONFIRMED
  );
  return (r && r.confirmed_at) || null;
}

/**
 * The pot a collector receives for one round in Model B / direct terms:
 * (contributor_count − 1) × expected_contribution — i.e. everyone BUT the round's
 * collector contributes. Pure and payout-free, so it works for a direct group that
 * has no payout row. Returns 0 for a missing round or unknown counts/amounts.
 * NOTE: this is the EXACT pot only while contributions are fixed per group (the
 * current model). If contribution amounts ever vary, sum actual confirmed payments
 * instead (banked upgrade for the member figure; the admin figure already does so).
 */
export function roundPot(round) {
  if (!round) return 0;
  const contributors = num(round.contributor_count);
  const contribution = num(round.expected_contribution);
  if (contributors == null || contribution == null) return 0;
  return Math.max(0, contributors - 1) * contribution;
}

/**
 * The total a member has COLLECTED (received) in the given cycle, across all of the
 * member's hands. OR pattern, provably Model-B-safe:
 *   byPayout > 0 ? byPayout : <sum of confirmed collection-round pots>
 * Model B: a real collection always has a nonzero confirmed/advanced payout, so
 * byPayout dominates and the value is byte-identical to the old payout-only sum —
 * the round fallback is unreachable. Direct groups have no payout (byPayout === 0),
 * so it falls back to (N−1) × contribution for each confirmed round the member
 * collected. Pure; uses only rows already in the member's scope (no new fetch).
 */
export function memberCollectedAmount(hands = [], cycleNumber, payouts = [], rounds = []) {
  if (cycleNumber == null) return 0;
  const handIds = new Set((hands || []).map((h) => String(h.id)));
  const byPayout = payouts
    .filter((p) => COLLECTED_PAYOUT_STATUSES.includes(p.status)
      && handIds.has(String(p.hand_id)) && num(p.cycle_number) === num(cycleNumber))
    .reduce((sum, p) => sum + (Number(p.payout_amount) || 0), 0);
  if (byPayout > 0) return byPayout;
  return rounds
    .filter((r) => handIds.has(String(r.collector_hand_id))
      && num(r.cycle_number) === num(cycleNumber) && r.status === ROUND_STATUS.CONFIRMED)
    .reduce((sum, r) => sum + roundPot(r), 0);
}

/**
 * Admin-side gate: can a payout be sent to THIS hand right now?
 * True iff there is a sendable (open/funded, not yet paid_out) round whose
 * collector is this hand, it is fully funded, and no payout exists for it yet.
 */
export function canSendPayout(hand, rounds = [], payments = [], payouts = []) {
  if (!hand) return false;
  const scoped = scopeToGroup(rounds, hand.group_id);
  const sendable = [...scoped]
    .filter((r) => SENDABLE_STATUSES.includes(r.status))
    .sort((a, b) => num(b.cycle_number) - num(a.cycle_number) || num(a.round_number) - num(b.round_number))[0];
  if (!sendable) return false;
  if (!sameId(sendable.collector_hand_id, hand.id)) return false;
  if (!isCurrentRoundFunded(sendable, payments)) return false;
  if (payouts.some((p) => sameId(p.round_id, sendable.id))) return false;
  return true;
}

/**
 * The ONE resolver both member-side and admin-side views call. Returns exactly
 * one MEMBER_STATE for `hand`.
 *
 * Precedence (given a live round in cycle C):
 *   1. up_now   — hand is the live round's collector
 *   2. up_next  — hand collects in round C.round_number+1
 *   3. contribute_now — hand still owes this round's contribution (action first,
 *                       so already-collected members keep the pot funded too)
 *   4. collected — hand contributed AND already collected this cycle
 *   5. waiting   — hand contributed, nothing else to do this round
 * With NO live round: cycle_complete if the latest cycle's rounds are all
 * confirmed; otherwise waiting (e.g. before the first round is opened).
 *
 * @returns {string} a MEMBER_STATE value
 */
export function resolveMemberRoundState(hand, rounds = [], payments = [], payouts = []) {
  if (!hand) return MEMBER_STATE.WAITING;

  const scoped = scopeToGroup(rounds, hand.group_id);
  const live = pickLive(scoped);

  if (!live) {
    // No live round: cycle_complete if the latest cycle is fully confirmed,
    // otherwise waiting (e.g. before the first round is opened).
    return isCycleComplete(scoped) ? MEMBER_STATE.CYCLE_COMPLETE : MEMBER_STATE.WAITING;
  }

  const cycle = num(live.cycle_number);

  // 1. The live collector — exactly one hand hits this.
  if (sameId(live.collector_hand_id, hand.id)) return MEMBER_STATE.UP_NOW;

  // 2. The very next collector (preview) — positional, independent of dates.
  const nextRound = scoped.find(
    (r) => num(r.cycle_number) === cycle && num(r.round_number) === num(live.round_number) + 1
  );
  if (nextRound && sameId(nextRound.collector_hand_id, hand.id)) return MEMBER_STATE.UP_NEXT;

  // 3. Still owes this round's contribution → action first (keeps the pot funded).
  const payment = getRoundPayment(hand, live, payments);
  const hasActed = payment && ACTED_PAYMENT_STATUSES.includes(payment.status);
  if (!hasActed) return MEMBER_STATE.CONTRIBUTE_NOW;

  // 4. Contributed AND already had their turn this cycle.
  if (hasCollectedThisCycle(hand, cycle, payouts, scoped)) return MEMBER_STATE.COLLECTED;

  // 5. Contributed, nothing else to do this round.
  return MEMBER_STATE.WAITING;
}
